import { DEFAULT_LOCALE } from '@constants/locales';
import { QUERY_KEY } from '@constants/queries';
import { Title } from '@mantine/core';
import { PodcastHighlighted } from '@molecules/frontoffice/PodcastHighlighted';
import { dehydrate, QueryClient } from '@tanstack/react-query';
import { validateZodSchema } from '@utils/zodSchemas';
import { newsDetailsPageZodSchema } from '@utils/zodSchemas/details';
import { GetServerSideProps } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { CarouselEpisodes } from '@organisms/frontoffice/CarouselEpisodes';
import {
  fetchAllEpisodes,
  useFetchAllEpisodes,
} from '@hooks/queries/useFetchAllEpisodes';
import {
  fetchInfinitePodcasts,
  useFetchInfinitePodcasts,
} from '@hooks/queries/useFetchInfinitePodcasts';
import { PodcastGrid } from '@organisms/frontoffice/PodcastGrid';
import { PublicPodcast } from '@utils/typesPages/types';
import { LoadingStateComponent } from '@molecules/frontoffice/LoadingStateComponent';
import { EmptyStateComponent } from '@molecules/frontoffice/EmptyStateComponent';
import { SEO } from '@organisms/SEO';
import { fetchCategories } from '@hooks/queries/useFetchCategories';
import { fetchPublicConfiguration } from '@hooks/queries/useFetchPublicConfiguration';
import { fetchSEO, useFetchSEO } from '@hooks/queries/useFetchSEO';

export const getServerSideProps: GetServerSideProps = async ({ ...ctx }) => {
  const parsedQuery = validateZodSchema(newsDetailsPageZodSchema, ctx.params);
  const { data: parsedData } = parsedQuery;

  const queryClient = new QueryClient();

  const episodesPromise = queryClient.prefetchQuery({
    queryKey: [QUERY_KEY.FETCH_EPISODES_ALL_PUBLIC],
    queryFn: async () => {
      return await fetchAllEpisodes();
    },
  });

  const categoriesPromise = queryClient.prefetchQuery({
    queryKey: [QUERY_KEY.FETCH_CATEGORIES, { fetchAll: true }],
    queryFn: async () => {
      return await fetchCategories({ fetchAll: true });
    },
  });

  const configurationsPromise = queryClient.prefetchQuery({
    queryKey: [QUERY_KEY.FETCH_PUBLIC_CONFIGURATION],
    queryFn: async () => {
      return await fetchPublicConfiguration();
    },
  });

  const podcastsPromise = queryClient.prefetchInfiniteQuery({
    queryKey: [QUERY_KEY.FETCH_PODCASTS],
    initialPageParam: 1,
    queryFn: ({ pageParam }) => fetchInfinitePodcasts({ pageParam }),
  });

  const seoPromise = queryClient.prefetchQuery({
    queryKey: [QUERY_KEY.FETCH_SEO],
    queryFn: async () => {
      return await fetchSEO();
    },
  });

  await Promise.all([
    seoPromise,
    podcastsPromise,
    configurationsPromise,
    episodesPromise,
    categoriesPromise,
  ]);

  return {
    props: {
      ...(await serverSideTranslations(ctx.locale ?? DEFAULT_LOCALE, [
        'common',
        'podcast',
      ])),
      dehydratedState: dehydrate(queryClient),
      slug: parsedData?.slug ?? '',
    },
  };
};

const PodcastsPage: FC = () => {
  const { t } = useTranslation('podcast');
  const [formattedData, setFormattedData] = useState<PublicPodcast[]>([]);

  const {
    data: infinitePodcasts,
    hasNextPage,
    fetchNextPage,
    isLoading: isLoadingPodcasts,
    isFetched,
  } = useFetchInfinitePodcasts();

  const { data: episodes, isLoading: isLoadingEpisodes } =
    useFetchAllEpisodes();

  const { data: dataSEO, isLoading: isLoadingSEO } = useFetchSEO();

  const SEOPodcasts = dataSEO?.value.find((page) => page.name === 'podcasts');

  useEffect(() => {
    if (infinitePodcasts && infinitePodcasts.pages) {
      setFormattedData(
        infinitePodcasts.pages.flatMap((podcasts) => podcasts.data)
      );
    }
  }, [infinitePodcasts]);

  const hasSettled =
    isFetched && !isLoadingPodcasts && !isLoadingEpisodes && !isLoadingSEO;

  if (!hasSettled) {
    return <LoadingStateComponent />;
  }

  if (
    (hasSettled && !infinitePodcasts) ||
    (hasSettled && infinitePodcasts?.pages[0].meta.total_pages === 0)
  ) {
    return <EmptyStateComponent />;
  }

  return (
    <>
      <SEO
        title={SEOPodcasts?.title}
        keywords={SEOPodcasts?.keywords}
        description={SEOPodcasts?.description}
      />
      {formattedData[0] && <PodcastHighlighted podcast={formattedData[0]} />}
      {episodes?.data.length !== 0 && (
        <>
          <Title variant="h3Orange" mt={40}>
            {t('podcast.latestEpisodes')}
          </Title>
          <CarouselEpisodes episodes={episodes?.data} />
        </>
      )}
      {formattedData.slice(1).length > 0 && (
        <>
          <Title mt={40} mb={30} variant="h3Orange">
            {t('podcasts')}
          </Title>
          <PodcastGrid
            hasNextPage={hasNextPage}
            fetchNextPage={fetchNextPage}
            podcasts={formattedData.slice(1)}
          />
        </>
      )}
    </>
  );
};

export default PodcastsPage;
